<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import { Trash2 } from 'lucide-vue-next'

import DeleteConfirmationModal from '@/views/autopilot/Components/DeleteConfirmationModal.vue'

import { patchContentScope, useContentScopeSave } from '@/composables/contentScope'
import type { ContentScope } from '@/types/contentScope'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const notification = useNotification()
const { notifyCampaignError } = useEmailDomainGuard()
const { hasPermissionBySlug } = useAccess()

const {
  count,
  isLoadingCount,
  name,
  step,
  emailContentId,
  conversionDelay,
  launchChoice,
  dateCampaign,
  timeCampaign,
  selectedAudience,
  emailContentSubject,
  emailContentPreviewText,
  userStoresAudience,
  userStoresCount,
  steppers,
  steps,
  selectedTemplateName,
  buildLaunchDate,
  fetchAllTemplates,
  fetchUserStoresAudience,
  scope
} = useCampaignEmailForm()

const { hasRequiredMandate, hasEmailMandate, canLaunchEmailCampaign, fetchMandateStatus } = useMandate()
const { emailSettings, getEmailSettings } = useEmailSettings()

const senderDomain = computed(() => emailSettings.value?.sender_domain)
const senderDomainVerified = computed(() => senderDomain.value?.is_verified ?? true)

const campaignId = computed(() => Number(route.params.campaignEmailId))

const loading = ref(true)
const processing = ref(false)
const savingDraft = ref(false)

const saveScope = useContentScopeSave()

const updateScope = async (value: ContentScope) => {
  scope.value = await saveScope(() => patchContentScope('campaigns', campaignId.value, value))
}

const hydrateFromCampaign = (campaign: any) => {
  name.value = campaign.name || ''
  scope.value = campaign.scope ?? null
  emailContentId.value = campaign.emailContentId ?? null
  conversionDelay.value = campaign.conversionDelay ?? 7

  if (campaign.launchDate || campaign.sentAt) {
    const date = dayjs(campaign.launchDate || campaign.sentAt)
    if (date.isValid()) {
      launchChoice.value = 'later'
      dateCampaign.value = date.format('DD/MM/YYYY')
      timeCampaign.value = { hour: date.hour(), minute: date.minute() }
    }
  }

  const stores = (campaign.stores || []).map((s: any) => ({
    value: s.id,
    label: s.name,
    smsName: s.smsName ?? s.sms_name ?? ''
  }))

  const segmentIds: number[] = []
  const excludedSegmentIds: number[] = []
  for (const target of campaign.targets || []) {
    if (target.selectorKey === 'segment_id') segmentIds.push(Number(target.selectorValue))
    if (target.selectorKey === 'excluded_segment_id') excludedSegmentIds.push(Number(target.selectorValue))
  }

  selectedAudience.value = {
    availableLists: selectedAudience.value.availableLists,
    stores,
    lists: segmentIds,
    excludedLists: excludedSegmentIds
  }
}

const fetchCampaign = async () => {
  try {
    const { data } = await axios.get(`/campaigns/${campaignId.value}`)
    const campaign = data.data
    if (!campaign.isDraft) {
      notification.create('error', {
        text: t('pages.campaignEmail.edit.notFound'),
        timeout: 5000
      })
      router.replace({ name: 'CampaignEmailListPage' })
      return
    }
    hydrateFromCampaign(campaign)
  } catch (error: any) {
    notification.create('error', {
      text: error?.response?.data?.message || t('pages.campaignEmail.edit.loadError'),
      persist: true
    })
    router.replace({ name: 'CampaignEmailListPage' })
  } finally {
    loading.value = false
  }
}

const buildUpdateParams = () => {
  const storeIds = selectedAudience.value.stores.map((s) => String(s.value))

  return {
    name: name.value,
    ...(scope.value && { scope: scope.value }),
    emailContentId: emailContentId.value,
    launchDate: buildLaunchDate(),
    conversionDelay: conversionDelay.value,
    audience: {
      storeIds,
      segmentIds: selectedAudience.value.lists,
      excludedSegmentIds: selectedAudience.value.excludedLists
    }
  }
}

const saveDraft = async () => {
  if (processing.value || savingDraft.value) return
  savingDraft.value = true

  try {
    await axios.put(`/campaigns/${campaignId.value}`, buildUpdateParams())
    notification.create('success', {
      text: t('pages.campaignEmail.create.notifications.draftSuccess'),
      timeout: 5000
    })
  } catch (error: any) {
    notification.create('error', {
      text: error?.response?.data?.message || t('pages.campaignEmail.create.notifications.draftError'),
      persist: true
    })
  } finally {
    savingDraft.value = false
  }
}

const canDelete = computed(() => hasPermissionBySlug('campaign.create'))
const isDeleteModalOpen = ref(false)
const deleting = ref(false)

const confirmDeleteCampaign = async () => {
  if (deleting.value) return
  deleting.value = true

  try {
    await axios.delete(`/campaigns/${campaignId.value}`)
    notification.create('success', {
      text: t('pages.campaignEmail.delete.success'),
      timeout: 3000
    })
    router.push({ name: 'CampaignEmailListPage' })
  } catch (error: any) {
    notification.create('error', {
      text: error?.response?.data?.message || t('pages.campaignEmail.delete.error'),
      persist: true
    })
  } finally {
    deleting.value = false
    isDeleteModalOpen.value = false
  }
}

const launchCampaign = async () => {
  if (processing.value || savingDraft.value || !canLaunchEmailCampaign.value) return
  processing.value = true

  try {
    await axios.put(`/campaigns/${campaignId.value}`, buildUpdateParams())
    const { data } = await axios.post(`/campaigns/${campaignId.value}/launch`)
    notification.create('success', {
      text: t('pages.campaignEmail.create.notifications.launchSuccess'),
      timeout: 5000
    })
    router.push({
      name: 'CampaignDetail',
      params: { campaignId: data.data.id },
      query: { type: 'email' }
    })
  } catch (error: any) {
    notifyCampaignError(error, t('pages.campaignEmail.create.notifications.launchError'))
  } finally {
    processing.value = false
  }
}

onMounted(async () => {
  await Promise.all([fetchUserStoresAudience(), fetchAllTemplates(), fetchMandateStatus(), getEmailSettings()])
  await fetchCampaign()
})
</script>

<template>
  <div>
    <TheLoadingPage v-if="loading" />
    <div v-else>
      <TheDetailHeader
        :title="t('pages.campaignEmail.create.title')"
        :parent="{ label: t('pages.campaignEmail.title'), to: { name: 'CampaignEmailListPage' } }"
      >
        <template #badge>
          <ContentScopeDetailBadge :scope subject="campaign" :save="updateScope" />
        </template>
        <template #actions>
          <Button variant="outline" :disabled="processing || savingDraft || !name" @click="saveDraft">
            <Spinner v-if="savingDraft" class="mr-2" />
            {{ t('pages.campaignEmail.edit.saveDraft') }}
          </Button>

          <Button
            v-if="canDelete"
            variant="outline-destructive"
            :disabled="processing || savingDraft || deleting"
            @click="isDeleteModalOpen = true"
          >
            <Trash2 />
            {{ t('global.words.delete') }}
          </Button>
        </template>
      </TheDetailHeader>

      <DeleteConfirmationModal
        :is-open="isDeleteModalOpen"
        :handle-close="() => (isDeleteModalOpen = false)"
        :title="t('pages.campaignEmail.delete.confirmTitle')"
        @on-confirm="confirmDeleteCampaign"
      />

      <div class="mb-12 hidden px-4 sm:block sm:px-12">
        <BaseStepper v-model="step" :steps="steps" />
      </div>

      <component
        :is="steppers[step]"
        :key="step"
        v-model:step="step"
        v-model:count="count"
        v-model:is-loading-count="isLoadingCount"
        v-model:name="name"
        v-model:selected-audience="selectedAudience"
        v-model:email-content-id="emailContentId"
        v-model:conversion-delay="conversionDelay"
        v-model:launch-choice="launchChoice"
        v-model:date-campaign="dateCampaign"
        v-model:time-campaign="timeCampaign"
        :user-stores-audience="userStoresAudience"
        :user-stores-count="userStoresCount"
        :selected-template-name="selectedTemplateName"
        :email-subject="emailContentSubject"
        :email-preview-text="emailContentPreviewText"
        :is-processing="processing"
        :is-saving-draft="savingDraft"
        :has-required-mandate="hasRequiredMandate"
        :has-email-mandate="hasEmailMandate"
        :can-launch-email-campaign="canLaunchEmailCampaign"
        :sender-domain-verified="senderDomainVerified"
        :sender-domain-id="senderDomain?.id ?? null"
        @on-create="launchCampaign"
        @on-save-draft="saveDraft"
      />
    </div>
  </div>
</template>
